import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useAxios } from './use-axios';

const MONITOR_ALERT_EMAILS_QUERY_KEY = ['settings', 'config', 'monitor-alert-emails'];

interface ApiSuccessResponse<T> {
  status: 'success';
  data: T;
}

interface MonitorAlertEmailsData {
  emails: string[];
}

function getErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    if (error.response?.status === 400)
      return 'Please enter valid email addresses';
    if (error.response?.status === 401 || error.response?.status === 403) {
      return 'You do not have permission to perform this action';
    }
  }
  return 'An unexpected error occurred. Please try again';
}

/** Recipients emailed when the local scan worker box goes down (live mode). */
export function useMonitorAlertEmails() {
  const axios = useAxios();
  return useQuery({
    queryKey: MONITOR_ALERT_EMAILS_QUERY_KEY,
    queryFn: async () => {
      try {
        const { data } = await axios.get<ApiSuccessResponse<MonitorAlertEmailsData>>(
          '/settings/config/monitor-alert-emails',
        );
        return data.data.emails;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    gcTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
}

export function useSetMonitorAlertEmails() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (emails: string[]) => {
      try {
        const { data } = await axios.put<ApiSuccessResponse<MonitorAlertEmailsData>>(
          '/settings/config/monitor-alert-emails',
          { emails },
        );
        return data.data.emails;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    onMutate: async (emails) => {
      await queryClient.cancelQueries({ queryKey: MONITOR_ALERT_EMAILS_QUERY_KEY });
      const previous = queryClient.getQueryData<string[]>(MONITOR_ALERT_EMAILS_QUERY_KEY);
      queryClient.setQueryData<string[]>(MONITOR_ALERT_EMAILS_QUERY_KEY, emails);
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(MONITOR_ALERT_EMAILS_QUERY_KEY, context.previous);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: MONITOR_ALERT_EMAILS_QUERY_KEY });
    },
  });
}
